/* eslint-disable import/no-unresolved */
/* eslint-disable no-plusplus */
/* eslint-disable complexity */
/* eslint-disable max-lines */
import React, { useMemo, useState, useCallback } from 'react';
import { describe, PropTypes } from '@elliemae/ds-props-helpers';
import { styled, border, truncate, withTheme, __UNSAFE_SPACE_TO_DIMSUM } from '@elliemae/ds-system';
import { TruncatedExpandableText } from '@elliemae/ds-truncated-expandable-text';
import { Grid } from '@elliemae/ds-grid';
import { ChevronSmallDown } from '@elliemae/ds-icons';
import { DSButton } from '@elliemae/ds-legacy-button-v1';
import { DSMobileSeparator as MobileSeparator } from '../MobileSeparator/index.js';

const StyledTitle = styled(Grid)`
  font-size: 22px;
  font-weight: ${(props) => props.theme.fontWeights.regular};
  color: ${(props) => props.theme.colors.neutral['700']};
  min-height: ${(props) => (props.withBreadcrumb ? 'auto' : '44px')};
  padding-top: ${(props) => (props.withBreadcrumb ? '0' : '6px')};
  line-height: 1.15;
`;

const StyledBreadCrumb = styled.div`
  font-size: 16px;
  font-weight: ${(props) => props.theme.fontWeights.regular};
  color: ${(props) => props.theme.colors.neutral['600']};
  ${truncate()}
`;

const Label = styled.div`
  color: ${(props) => props.theme.colors.neutral['500']};
  ${truncate()}
`;

const Value = styled.div`
  ${truncate()}
  color: ${(props) => props.theme.colors.neutral['600']};
  ${(props) => (props.withMarginRight ? `margin-right: 4px;` : '')}
  margin-top: ${(props) => (props.withBreadcrumb ? '12px' : '0')};
`;

const Wrapper = styled(Grid)<{ withBlueBorder: boolean }>`
  border-bottom: ${({ theme, withBlueBorder }) =>
    border(withBlueBorder ? theme.colors.brand['700'] : theme.colors.neutral['300'])};
`;

const MobilePageHeader = ({
  pageTitle = null,
  withBlueBorder = false,
  contextMenu = null,
  firstAction = null,
  secondAction = null,
  backArrow = null,
  breadCrumb = null,
  onOpenContextMenu = () => null,
  value,
  label,
  theme,
}) => {
  const hasValue = value === 0 || value;

  const countActions = useMemo(() => {
    let count = 0;
    if (firstAction) count++;
    if (secondAction) count++;
    return count;
  }, [firstAction, secondAction]);
  const [open, setOpen] = useState();
  const handleContext = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const separator = (
    <Grid height="32px" alignItems="center">
      <Grid height="24px">
        <MobileSeparator direction="vertical" margin="xxs" color={['neutral', '300']} />
      </Grid>
    </Grid>
  );
  const cols = [1, 'auto'];
  const cols2 = ['auto'];
  if (countActions) cols.push('auto');
  if (hasValue) cols2.push('auto');
  if (label) cols2.push('auto');

  return (
    <Wrapper
      cols={cols}
      pl={theme.space.xs}
      pr={theme.space.xs}
      alignItems="flex-start"
      width="100%"
      data-testid="page-header-wrapper"
      withBlueBorder={withBlueBorder}
    >
      <Grid alignItems="flex-start" justifyContent="flex-start" cols={cols2}>
        <Grid
          cols={backArrow ? ['auto', 1] : [1]}
          alignItems="flex-start"
          pr={countActions === 0 && !hasValue && !label ? __UNSAFE_SPACE_TO_DIMSUM(theme.space.s) : 0}
        >
          {backArrow && (
            <Grid height={breadCrumb ? '56px' : '44px'} alignItems="center" pr="16px">
              {backArrow}
            </Grid>
          )}
          <Grid flexGrow={0} pt={!breadCrumb ? 0 : theme.space.xxs}>
            <Grid>
              <Grid>
                {breadCrumb && <StyledBreadCrumb data-testid="page-header-breadcrum">{breadCrumb}</StyledBreadCrumb>}
                <StyledTitle
                  cols={['auto', '1fr']}
                  gutter="xxxs"
                  maxWidth="100%"
                  alignItems="flex-start"
                  withBreadcrumb={!!breadCrumb}
                  data-testid="page-header-title"
                >
                  <Grid pt={breadCrumb ? 0 : theme.space.xxxs} alignItems="flex-start" justifyItems="center">
                    <TruncatedExpandableText value={pageTitle} />
                  </Grid>
                  {contextMenu ? (
                    <Grid alignItems="center">
                      <DSButton
                        buttonType="text"
                        icon={<ChevronSmallDown />}
                        size="s"
                        onClick={() => {
                          handleContext();
                          onOpenContextMenu();
                        }}
                        containerProps={{ 'data-testid': 'trigger-context-menu' }}
                      />
                      {React.cloneElement(contextMenu, {
                        open: contextMenu.props.open !== undefined ? contextMenu.props.open : open,
                        onChange: (event, item) => {
                          if (contextMenu.props.onChange) contextMenu.props.onChange(event, item);
                          handleContext();
                        },
                      })}
                    </Grid>
                  ) : null}
                </StyledTitle>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {hasValue && (
          <Grid height={breadCrumb ? '56px' : '44px'} alignItems="center" cols={['auto', 'auto']}>
            {separator}
            <Value withMarginRight={!!label} data-testid="page-header-value">
              {value}
            </Value>
          </Grid>
        )}
        {label && (
          <Grid height={breadCrumb ? '56px' : '44px'} alignItems="center">
            <Label withBreadcrumb={!!breadCrumb} data-testid="page-header-label">
              {label}
            </Label>
          </Grid>
        )}
      </Grid>
      {countActions > 0 && (
        <Grid
          height={breadCrumb ? '56px' : '44px'}
          alignItems="center"
          cols={countActions === 1 ? ['auto'] : ['auto', 'auto', 'auto']}
        >
          {firstAction && <Grid>{firstAction}</Grid>}
          {countActions === 2 && separator}
          {secondAction && <Grid>{secondAction}</Grid>}
        </Grid>
      )}
    </Wrapper>
  );
};

const pageHeaderProps = {
  pageTitle: PropTypes.string.description('Page title').isRequired,
  contextMenu: PropTypes.element.description('Context Menu to open'),
  onOpenContextMenu: PropTypes.func.description('function called when context menu opens'),
  firstAction: PropTypes.element.description('Action Button'),
  secondAction: PropTypes.element.description('Action Button'),
  backArrow: PropTypes.element.description('Back Arrow Button'),
  breadCrumb: PropTypes.string.description('BreadCrumbs title'),
  value: PropTypes.number.description('Page summary value'),
  label: PropTypes.string.description('Page summary label'),
  theme: PropTypes.object.description('dimsum theme'),
};

MobilePageHeader.displayName = 'MobilePageHeader';
const PageHeaderWithSchema = describe(MobilePageHeader);
PageHeaderWithSchema.propTypes = pageHeaderProps;

const DSMobilePageHeader = withTheme(MobilePageHeader);

export { DSMobilePageHeader, PageHeaderWithSchema };
